import { z } from 'zod';

/**
 * Money is integer **micro-dollars** everywhere except the configuration boundary.
 *
 * A turn's cost is a sum of many small products — tokens times a per-million rate — and the caps
 * are compared against that sum after every step. In binary floating point those additions drift,
 * and a cap is exactly the kind of comparison where drift is not cosmetic: it decides whether the
 * next provider call happens. Integers make the ledger's sum and the cap's comparison exact, and
 * a micro-dollar is finer than any per-call cost worth distinguishing.
 */
export const MICRO_USD_PER_USD = 1_000_000;

/** Dollars come from configuration and nowhere else, so this is the only conversion in. */
export const usdToMicroUsd = (usd: number): number => Math.round(usd * MICRO_USD_PER_USD);

export const formatMicroUsd = (microUsd: number): string =>
  `$${(microUsd / MICRO_USD_PER_USD).toFixed(4)}`;

/**
 * What a provider reported for one call.
 *
 * `cachedInputTokens` is a *subset* of `inputTokens` — that is how the AI SDK reports it — so the
 * non-cached count is the difference rather than a third number. Cache *writes* are not modelled:
 * providers price them within a few percent of ordinary input tokens, and inventing a third rate
 * to chase that would add a knob whose value nobody could check.
 */
export interface TokenUsage {
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
}

/**
 * A model's price, as micro-dollars per million tokens, which is the unit published rates come in.
 * Keeping the rate in the same integer currency as the ledger means a cost is one multiplication
 * and one division rather than a chain of unit conversions.
 */
export interface ModelRate {
  inputMicroUsdPerMillion: number;
  cachedInputMicroUsdPerMillion: number;
  outputMicroUsdPerMillion: number;
}

const TOKENS_PER_MILLION = 1_000_000;

/**
 * Rounded up, deliberately. A cap exists to bound spend, so every rounding decision in the chain
 * that feeds it should err towards charging more rather than less — a cap that trips a hundredth
 * of a cent early costs nothing, and one that trips late is the failure this whole module exists
 * to prevent.
 */
const rateCost = (tokens: number, microUsdPerMillion: number): number =>
  Math.ceil((Math.max(0, tokens) * microUsdPerMillion) / TOKENS_PER_MILLION);

export const costMicroUsd = (usage: TokenUsage, rate: ModelRate): number => {
  const cached = Math.min(Math.max(0, usage.cachedInputTokens), Math.max(0, usage.inputTokens));
  const fresh = Math.max(0, usage.inputTokens) - cached;

  return (
    rateCost(fresh, rate.inputMicroUsdPerMillion) +
    rateCost(cached, rate.cachedInputMicroUsdPerMillion) +
    rateCost(usage.outputTokens, rate.outputMicroUsdPerMillion)
  );
};

/**
 * A provider that reports no token counts is a real case — a stream that failed part-way, a model
 * whose usage block the adapter could not read — and it must not read as a free call. The counts
 * are taken as zero because there is nothing better to use, and the fact that they were missing
 * travels with the ledger row so a day of quietly uncounted spend is visible rather than inferred
 * from a suspiciously small total.
 */
export interface NormalizedUsage {
  usage: TokenUsage;
  /**
   * False when the provider left the input or output count unreported.
   *
   * A missing cache breakdown is ordinary — most providers have no prompt caching to report — so
   * treating its absence as an incomplete reading would flag almost every call and teach whoever
   * reads the ledger to ignore the flag. Only the two counts that decide the cost are checked.
   */
  complete: boolean;
}

export const normalizeUsage = (reported: {
  inputTokens?: number | undefined;
  cachedInputTokens?: number | undefined;
  outputTokens?: number | undefined;
}): NormalizedUsage => ({
  usage: {
    inputTokens: reported.inputTokens ?? 0,
    cachedInputTokens: reported.cachedInputTokens ?? 0,
    outputTokens: reported.outputTokens ?? 0,
  },
  complete: reported.inputTokens !== undefined && reported.outputTokens !== undefined,
});

/**
 * The three limits the design makes non-negotiable, in the units the worker enforces them in.
 *
 * They are one object rather than three parameters because they are enforced together and a call
 * site that could pass two of them is a call site that can forget the third.
 */
export interface SpendCaps {
  /** Provider calls one turn may make. Reaching it truncates the turn rather than costing more. */
  maxIterations: number;
  perTurnMicroUsd: number;
  perDayMicroUsd: number;
}

export const spendCapsSchema = z.object({
  maxIterations: z.int().positive(),
  perTurnMicroUsd: z.int().positive(),
  perDayMicroUsd: z.int().positive(),
});

export const SPEND_BREACH_TURN = 'turn_spend_cap_exceeded';
export const SPEND_BREACH_DAY = 'daily_spend_cap_exceeded';

export interface SpendBreach {
  code: typeof SPEND_BREACH_TURN | typeof SPEND_BREACH_DAY;
  spentMicroUsd: number;
  capMicroUsd: number;
}

/**
 * Whether spending so far has crossed a cap. Checked *before* deciding to make another call, so
 * "at the cap" counts as crossed: a turn that has spent exactly its allowance has none left.
 *
 * The day cap is reported first when both are crossed, because the two say different things to
 * whoever reads the terminal event. A turn cap means this turn asked for too much and a shorter
 * one would work; a day cap means the environment is out of budget and nothing will work until
 * tomorrow. Naming the recoverable one while the unrecoverable one also holds would send someone
 * to retry a message that cannot succeed.
 */
export const findSpendBreach = ({
  turnMicroUsd,
  dayMicroUsd,
  caps,
}: {
  turnMicroUsd: number;
  dayMicroUsd: number;
  caps: SpendCaps;
}): SpendBreach | null => {
  if (dayMicroUsd >= caps.perDayMicroUsd) {
    return { code: SPEND_BREACH_DAY, spentMicroUsd: dayMicroUsd, capMicroUsd: caps.perDayMicroUsd };
  }

  if (turnMicroUsd >= caps.perTurnMicroUsd) {
    return {
      code: SPEND_BREACH_TURN,
      spentMicroUsd: turnMicroUsd,
      capMicroUsd: caps.perTurnMicroUsd,
    };
  }

  return null;
};

export const describeSpendBreach = (breach: SpendBreach): string =>
  breach.code === SPEND_BREACH_DAY
    ? `this environment has reached its daily spend limit of ${formatMicroUsd(breach.capMicroUsd)}`
    : `this turn reached its spend limit of ${formatMicroUsd(breach.capMicroUsd)}`;

/**
 * The `spend_capped` event: a cap crossed by a turn that still finished what it was asked to do.
 *
 * Non-terminal, so a client must not treat it as a failure — the turn that emits this also emits
 * `done`, and the reply it produced is complete. It says the *next* turn may be refused.
 */
export const spendCappedEventPayloadSchema = z.object({
  code: z.enum([SPEND_BREACH_TURN, SPEND_BREACH_DAY]),
  message: z.string().min(1),
  spent_micro_usd: z.int().nonnegative(),
  cap_micro_usd: z.int().nonnegative(),
});
export type SpendCappedEventPayload = z.infer<typeof spendCappedEventPayloadSchema>;

/** What a turn spent, as the `usage` turn event carries it to the client. */
export const usageEventPayloadSchema = z.object({
  model: z.string().min(1),
  input_tokens: z.int().nonnegative(),
  cached_input_tokens: z.int().nonnegative(),
  output_tokens: z.int().nonnegative(),
  cost_micro_usd: z.int().nonnegative(),
  turn_total_micro_usd: z.int().nonnegative(),
});
export type UsageEventPayload = z.infer<typeof usageEventPayloadSchema>;
